import type { JobForYou } from '../types/jobs';
import { calculateConfidenceScore } from './jobConfidence';

/** Client-facing column order and header labels (first CSV row). */
const CLIENT_CSV_HEADERS = [
  'Job title',
  'Company',
  'Job URL',
  'Posted',
  'Location',
  'Platform',
  'Network score',
  'Connection tier',
  'Role fit',
  'Overall match (%)',
] as const;

function escapeCsvField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function formatJobLocation(job: JobForYou): string {
  const loc = job.location;
  if (loc == null) return '';
  if (Array.isArray(loc)) return loc.join(' | ');
  return String(loc);
}

function jobApplicationUrl(job: JobForYou): string {
  const a = (job.posting || '').trim();
  if (a) return a;
  return (job.searchURLPrefix || '').trim();
}

function roleFitLabel(job: JobForYou): string {
  if (job.jobFit === 'high') return 'High';
  if (job.jobFit === 'medium') return 'Medium';
  if (job.jobFit === 'low') return 'Low';
  return '';
}

function clientRowForJob(
  job: JobForYou,
  networkWeight: number,
  fitWeight: number,
): Record<(typeof CLIENT_CSV_HEADERS)[number], string> {
  const calculated = calculateConfidenceScore(
    job.connectionScore,
    job.jobFit,
    networkWeight,
    fitWeight,
    job.connectionStrength,
  );
  const overall =
    job.confidenceScore != null && job.confidenceScore !== undefined
      ? job.confidenceScore
      : calculated;

  return {
    'Job title': job.postingTitle ?? '',
    Company: job.sourceCompany ?? '',
    'Job URL': jobApplicationUrl(job),
    Posted: job.postingDate != null ? String(job.postingDate) : '',
    Location: formatJobLocation(job),
    Platform: job.platform ?? '',
    'Network score':
      job.connectionScore != null && job.connectionScore !== undefined
        ? String(job.connectionScore)
        : '',
    'Connection tier': job.connectionStrength ?? '',
    'Role fit': roleFitLabel(job),
    'Overall match (%)': overall != null ? String(overall) : '',
  };
}

/**
 * Build CSV string (UTF-8 with BOM for Excel). Client-facing columns only.
 */
export function buildJobsCsv(
  jobs: JobForYou[],
  networkWeight: number = 70,
  fitWeight: number = 30,
): string {
  const header = CLIENT_CSV_HEADERS.join(',');
  const lines = jobs.map((job) => {
    const r = clientRowForJob(job, networkWeight, fitWeight);
    return CLIENT_CSV_HEADERS.map((col) => escapeCsvField(r[col])).join(',');
  });
  const body = [header, ...lines].join('\r\n');
  return '\uFEFF' + body;
}

export function downloadJobsCsv(
  jobs: JobForYou[],
  filenameBase: string,
  networkWeight?: number,
  fitWeight?: number,
): void {
  const csv = buildJobsCsv(jobs, networkWeight ?? 70, fitWeight ?? 30);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenameBase}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
